import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { FuseConfig, FuseConfigService, Scheme, Theme, Themes } from '@fuse/services/config';

import { Subject, takeUntil } from 'rxjs';
import { SettingsService } from './settings.service';

@Component({
    selector: 'settings',
    templateUrl: './settings.component.html',
    styles: [
        `
            settings {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }

            @media (screen and min-width: 1280px) {

                empty-layout + settings .settings-cog {
                    right: 0 !important;
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'settings',
    imports: [MatIconModule, FuseDrawerComponent, MatButtonModule, NgClass, MatTooltipModule]
})
export class SettingsComponent implements OnInit, OnDestroy
{
    config: FuseConfig;
    flashMessage: 'success' | 'error' | null = null;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    themes: Themes;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseConfigService: FuseConfigService,
        private _settingsService: SettingsService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) =>
            {
                // Store the config
                this.config = config;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    saveTheme(): void {
        this._settingsService.updateTheme(JSON.stringify(this.config))
            .subscribe(
                (response) => {
                    if (response.errors) {

                        // Show error
                        this.showFlashMessage('error');

                    } else {

                        // Save in local storage
                        localStorage.setItem('theme_options', JSON.stringify(this.config))

                        // Show a success message
                        this.showFlashMessage('success');
                    }
                },
                (response) => {

                    // Show error
                    this.showFlashMessage('error');
                }
            );
    }

    /**
     * Set the layout on the config
     *
     * @param layout
     */
    setLayout(layout: string): void
    {
        // Clear the 'layout' query param to allow layout changes
        this._router.navigate([], {
            queryParams        : {
                layout: null,
            },
            queryParamsHandling: 'merge',
        }).then(() =>
        {
            // Set the config
            this._fuseConfigService.config = {layout};
        });
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void
    {
        this._fuseConfigService.config = {scheme};
    }

    /**
     * Set the theme on the config
     *
     * @param theme
     */
    setTheme(theme: Theme): void
    {
        this._fuseConfigService.config = {theme};
    }

     /**
     * Show flash message
     */
        showFlashMessage(type: 'success' | 'error'): void {
            // Show the message
            this.flashMessage = type;

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // Hide it after 3 seconds
            setTimeout(() => {

                this.flashMessage = null;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            }, 4000);
        }
}
